import { font, palette, zIndex } from 'styles/Global/globalStyles';
import styled from 'styled-components';
import { RowDivider } from 'styles/Global/commonStyles';
import BasicButton from 'components/Buttons/BasicButton';
import { commonText } from 'constants/commonConstants';
import { BackgroundColor, BorderColor } from 'styles/Global/colorSemantic';
import { AgeAndBirthDateWrapper, CloseButtonWrapper, TextInDetailPatientPageModal } from 'styles/Modal/modalStyle';
import { Body3_Medium_Input, Body3Medium } from 'styles/Global/typography';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import CloseDialogue from 'assets/Icons/DeleteIcon/CloseDialogue';
import { ModalBackground, ModalLayout, ModalLeftTitle } from 'components/Modal/ModalForms';
import { InputContainerStyleProps } from 'models/Common/inputTypes';
import { InputWithLabelIconWrapper } from 'styles/Input/inputStyle';
import TimeInCalendar from 'components/Hospital/TimeInCalendar';
import { AddDiagnosisPageCalendar } from 'components/Common/Calendar';
import { calculateAge } from 'utils/calculateAge';
import { FillOutDialogueScheduleProps } from 'models/Modal/fillOutDialogTypes';
import IcCalendar from 'assets/Icons/Calendar/IcCalendar';
import moment from 'moment';
import { getValidDateString } from 'utils/calculateTime';

const FillOutDialogueSchedule = ({
    diagnosisInfo,
    selectedDate,
    selectedTime,
    setSelectedDate,
    setSelectedTime,
    updateDiagnosisInfoApiCall,
    initDate,
    onModalClose,
    label,
    title,
}: FillOutDialogueScheduleProps) => {
    const { birthDate, ageString } = calculateAge(diagnosisInfo.patientIdentityNumber!);

    return (
        <ModalBackground>
            <ModalLayout $width='660px' $gap='36px'>
                <ModalInnerLayout>
                    <ModalLeftTitle>{title}</ModalLeftTitle>
                    <ModalBodyLayout>
                        <PatientInfoHeaderContainer>
                            <InfoWrapper>
                                <TextInDetailPatientPageModal>{diagnosisInfo.patientName}</TextInDetailPatientPageModal>
                                <TextInDetailPatientPageModal>
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {diagnosisInfo.patientGender === 'male' ? <GenderMan $width='16px' $height='16px' /> : <GenderWoman $width='16px' $height='16px' />}
                                    </span>
                                </TextInDetailPatientPageModal>
                                <AgeAndBirthDateWrapper>
                                    <span>{ageString}</span>
                                    <span>({birthDate})</span>
                                </AgeAndBirthDateWrapper>
                            </InfoWrapper>
                        </PatientInfoHeaderContainer>
                        <RowDivider $backgroundcolor={BorderColor.color_border_strong} />
                        <Container>
                            <Label>{label}</Label>
                            <InputAndCalendar>
                                <InputWrapper>
                                    <BasicInput padding='12px 20px' $height='100%' $width='100%' value={selectedDate + ' ' + selectedTime} readOnly />

                                    <InputWithLabelIconWrapper style={{ cursor: 'default' }} $top='12px'>
                                        <IcCalendar />
                                    </InputWithLabelIconWrapper>
                                </InputWrapper>
                                <CalenderContainer>
                                    <CalenderWrapper>
                                        <AddDiagnosisPageCalendar setSelectedDate={setSelectedDate} selectedDate={getValidDateString(selectedDate!)} />
                                    </CalenderWrapper>
                                    <TimeWrapper>
                                        <ResetTimeButton onClick={initDate}>오늘로 이동</ResetTimeButton>
                                        <TimeInCalendar setSelectedTime={setSelectedTime} selectedTime={selectedTime!} selectedDate={selectedDate} serviceType={'counsel'} />
                                    </TimeWrapper>
                                </CalenderContainer>
                            </InputAndCalendar>
                        </Container>
                        <RowDivider $backgroundcolor={palette.lightGray} />
                    </ModalBodyLayout>
                </ModalInnerLayout>
                <ButtonContainer>
                    <BasicButton $width='160px' $height='100%' $type='anotherFill' onClick={onModalClose}>
                        {commonText.cancel}
                    </BasicButton>
                    <BasicButton $width='160px' $height='100%' $type='fill' onClick={updateDiagnosisInfoApiCall}>
                        변경하기
                    </BasicButton>
                </ButtonContainer>
                <CloseButtonWrapper onClick={onModalClose}>
                    <CloseDialogue />
                </CloseButtonWrapper>
            </ModalLayout>
        </ModalBackground>
    );
};

export default FillOutDialogueSchedule;

const ModalInnerLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const PatientInfoHeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50px;
    padding: 12px 20px;
    background-color: ${BackgroundColor.color_background_primary_normal};
    gap: 6px;
`;

const Container = styled.div`
    display: flex;
    width: 100%;
    gap: 12px;
`;

const Label = styled(Body3Medium)`
    display: flex;
    align-items: center;
    width: 136px;
    height: 48px;
    gap: 4px;
`;

const InputAndCalendar = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 440px;
    gap: 8px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    height: 48px;
`;

const ModalBodyLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 20px;
    gap: 14px;
`;
const InfoWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

const InputWrapper = styled.div<InputContainerStyleProps>`
    position: relative;
    width: 100%;
    height: 48px;
    overflow: visible;
`;

const CalenderContainer = styled.div`
    position: relative;
    display: flex;
    padding: 24px 24px 28px;
    width: 100%;
    height: 385px;
    border-radius: 4px;
    border: 1px solid ${BorderColor.color_border_normal};
    background-color: ${palette.white};
    z-index: ${zIndex.dropdown};
`;

const CalenderWrapper = styled.div`
    width: 280px;
    height: 100%;
    background-color: ${palette.white};
`;

const TimeWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 280px);
    height: 270px;
    margin-top: 51px;
    overflow-y: auto;
    gap: 6px;
`;

const ResetTimeButton = styled.button`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f5f5;
    width: 88px;
    height: 40px;
    font-size: ${font.size[14]};
    border-radius: 4px;
    right: 41px;
    top: 24px;
    &:hover {
        background-color: ${palette.gray};
    }
`;

const BasicInput = styled(Body3_Medium_Input)<InputContainerStyleProps>`
    display: flex;
    align-items: center;
    border-radius: 4px;
    border: 1px solid ${BorderColor.color_border_normal};
    padding: 18px 20px;
    height: ${(props) => props.$height || '60px'};
    width: ${(props) => props.$width || '100%'};
    letter-spacing: ${(props) => props.id === 'identityNumber' && '3px'};
    caret-color: ${(props) => props.$warning && 'red'};
    cursor: default;
    background-color: ${BackgroundColor.color_background_white};
    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;
